const isArray = require('lodash/isArray');
const first = require('lodash/first');
const flatMap = require('lodash/flatMap');

const expand = require('./expand');
const join = require('./join');

/**
 * Expand one or more query path expressions into an array of individual leaf (string) paths
 * @param {QueryPathSet|Array<QueryPathSet>} queryPathSet the query expression
 * to expand
 * @see {@link expand}
 * @returns {Array<LeafPath<string>>} the expanded array of leaf paths, as strings
 * @example
 * processAndExpandQueryPaths(fx.path[0].bid);
 * // => ['fx.path[0].bid']
 *
 * processAndExpandQueryPaths([
 *   'fx.path[0].bid',
 *   'fx.path[0].ask',
 *   'fx.path[1].bid',
 *   'fx.path[1].ask',
 * ]);
 * // =>  ['fx.path[0].bid', 'fx.path[0].ask', 'fx.path[1].bid', 'fx.path[1].ask']
 *
 * processAndExpandQueryPaths([ 'fx', 'path', 0, 'bid' ]);
 * // => [ 'fx.path[0].bid' ]
 *
 * processAndExpandQueryPaths([
 *   [ 'fx', 'path', 0, 'bid' ],
 *   [ 'fx', 'path', 0, 'ask' ],
 *   [ 'fx', 'path', 1, 'bid' ],
 *   [ 'fx', 'path', 1, 'ask' ],
 * ]);
 * // =>  ['fx.path[0].bid', 'fx.path[0].ask', 'fx.path[1].bid', 'fx.path[1].ask']
 */
function processAndExpandQueryPaths(queryPathSet) {
  // need array of strings or array of pathSet syntax
  let querySet = queryPathSet;
  if (!isArray(queryPathSet) || isPathSetSyntax(queryPathSet)) {
    querySet = [queryPathSet];
  }
  return expandRanges(querySet);
}

/**
 * Process a set of route path expressions, which can be individual strings, arrays of strings,
 * Falcor array syntax expressions or arrays thereof.
 * @param {RoutePathSet|Array<RoutePathSet>} routeParams the query expressions to
 * process
 * @returns {Array<RoutePathSet<string>>} the processed array of string paths
 * @example
 * processRouteParameters('route.string.path');
 * // => ['route.string.path']
 *
 * processRouteParameters(['route.string.path1', 'route.string.path2']);
 * // => ['route.string.path1', 'route.string.path2']
 *
 * processRouteParameters(['route', 'string', 'path']);
 * // => ['route.string.path']
 *
 * processRouteParameters([
 *   ['route', 'string', 'path1'], ['route', 'string', 'path2'],
 * ]);
 * // => ['route.string.path1', 'route.string.path2',]
 */
function processRouteParameters(routeParams) {
  // ensure we return an array of string paths
  if (!isArray(routeParams)) {
    // single string path
    return [routeParams];
  }
  const firstParam = first(routeParams, '');
  if (isArray(firstParam)) {
    // array of pathSet syntax
    return routeParams.map((pathSetArray) => join(pathSetArray));
  }
  if (isStringPath(firstParam)) {
    // array of string paths
    return routeParams;
  }
  // single pathSet syntax
  return [join(routeParams)];
}

function isPathSetSyntax(path) {
  // is falcor-style ['path', 'to', 'data']
  return isArray(path) && !isArray(first(path)) && !isStringPath(first(path, ''));
}

function isStringPath(path) {
  // is single string 'path.to.data'
  return !isArray(path) && (path.includes('.') || path.includes('['));
}

function expandRanges(pathSetArray) {
  return flatMap(pathSetArray, (pathSet) => expand(pathSet, true));
}

module.exports = {
  processAndExpandQueryPaths,
  processRouteParameters,
};
